import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import useU2000AxiosAuthenticated from '../u2000Core/axios/U2000UseAxiosAuthenticated';
import { U2000HandleApiError } from '../u2000Core/axios/U2000HandleApiError';
import { useToast } from '../u2000Core/context/toast/U2000ToastContext';
import type { U2000AutoCompleteSearchResult } from '../u2000Core/types/U2000AutoCompleteSearchResult';
import { u2000ExtractAutoCompleteResults } from '../u2000Core/utils/u2000ExtractAutoCompleteResults';
import {
  doFindEmployee,
  luHandleAppiError,
  type U3101AutoCompletCriterias,
  type U3101FindEmployeeItem,
} from './U3101Service';
import { NS } from './U3101';

/** Recherche employé via POST /U3101/findEmployee (criteriaLookup BL-0058). */
export function useU3101FindEmployeeHandlers() {
  const axios = useU2000AxiosAuthenticated();
  const { t } = useTranslation();
  const toast = useToast();

  const search = useCallback(
    async (criterias: U3101AutoCompletCriterias): Promise<U2000AutoCompleteSearchResult<U3101FindEmployeeItem>> => {
      try {
        const withPageSize = [...criterias];
        const hasPageSize = withPageSize.some((c) => c.fieldName === 'pageSize');
        if (!hasPageSize) {
          withPageSize.push({ fieldName: 'pageSize', value: 50 });
        }
        const response = await doFindEmployee(axios, withPageSize);
        return u2000ExtractAutoCompleteResults<U3101FindEmployeeItem>(response.data);
      } catch (error: unknown) {
        const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
        return { results: [], totalCount: 0, truncated: false };
      }
    },
    [axios, t, toast],
  );

  return { search };
}
